import React from 'react';
import { Box, Typography, Paper, useTheme } from '@mui/material';

interface EstimatedIncomeProps {
    current: number;
    total: number;
    amount: string;
    subtitle?: string;
    size?: number;
    thickness?: number;
    title?: string;
}

const EstimatedIncomeProgress: React.FC<EstimatedIncomeProps> = ({
    current,
    total,
    amount,
    subtitle = 'Según la tasa del día',
    size = 160,
    thickness = 16,
    title = 'Ingresos estimados'
}) => {
    const theme = useTheme();

    const percentage = Math.min(100, (current / total) * 100);

    const radius = (size - thickness) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    const completedColor = '#E6C068'; 
    const remainingColor = '#F2F2F2';

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                width: '100%'
            }}
        >
            <Typography variant="h6" component="h2" fontWeight="500" mb={2}>
                {title}
            </Typography>

            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: size,
                    height: size,
                    margin: '0 auto'
                }}
            >
                <svg
                    width={size}
                    height={size}
                    viewBox={`0 0 ${size} ${size}`}
                    style={{ position: 'absolute' }}
                >
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={remainingColor}
                        strokeWidth={thickness}
                    />
                </svg>

                <svg
                    width={size}
                    height={size}
                    viewBox={`0 0 ${size} ${size}`}
                    style={{
                        position: 'absolute',
                        transform: 'rotate(-90deg)'
                    }}
                >
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={completedColor}
                        strokeWidth={thickness}
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                    />
                </svg>

                <Box
                    sx={{
                        position: 'absolute',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        padding: 1
                    }}
                >
                    <Typography
                        variant="h4"
                        component="div"
                        fontWeight="bold"
                        sx={{ lineHeight: 1.2 }}
                    >
                        {amount}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            fontSize: '0.75rem',
                            mt: 0.5,
                            maxWidth: size * 0.6,
                            wordWrap: 'break-word',
                            textAlign: 'center'
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
};

export default EstimatedIncomeProgress;